import React from "react";
import { useForm } from "react-hook-form";
import CryptoJS from "crypto-js";
import styles from "./SignUp.module.scss";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { ApiError } from "../../services/api/BaseApi";
import { getPasswordStrengthResult, passwordRules } from "../../utils/passwordValidation";
import { useAlert } from "../../hooks/useAlert";
import PasswordInput from "../../components/PasswordInput/PasswordInput";
import { calculateAge } from "../../utils/calculateAge";

type SignUpForm = {
    username: string;
    email: string;
    confirmEmail: string;
    password: string;
    confirmPassword: string;
    dateOfBirth: string;
    termsAccepted: boolean;
};

const SignUp: React.FC = () => {
    const { login } = useUser();
    const navigate = useNavigate();
    const { alert } = useAlert();
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<SignUpForm>();

    const onSubmit = async (data: SignUpForm) => {
        try {
            const payload = {
                ...data,
                email: data.email.toLowerCase(),
                password: CryptoJS.SHA256(data.password).toString(),
                dateOfBirth: data.dateOfBirth,
                termsAccepted: data.termsAccepted,
            };
            const res = await api.app.registerUser(payload);
            navigate("/")
            alert({ message: `Email verification sent to ${data.email}`, type: 'success' });
        } catch (err: any) {
            console.error("Error submitting form:", err);
            if (err instanceof ApiError) {
                const data = err.data;
                alert({ message: data?.message || err.message || "Something went wrong. Please try again.", type: 'error' });
                return;
            }
            alert({ message: err?.response?.data?.message || "Something went wrong. Please try again.", type: 'error' });
        }
    };

    const password = watch("password", "");
    const email = watch("email", "");

    // Password strength logic
    const { score: strength, label: strengthLabel, color: strengthColor } = getPasswordStrengthResult(password);

    return (
        <div className={styles.signupPage}>
            <div className={styles.card}>
                <div className={styles.brand}>
                    <span className={styles.cursive}>Hippocampus Gaming</span>
                    <p className={styles.tagline}>Your gaming universe, all in one place</p>
                </div>

                <div className={styles.divider}>Create account</div>

                <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>
                    {/* Username */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="username">Username</label>
                        <input
                            id="username"
                            type="text"
                            placeholder="coolgamer42"
                            {...register("username", {
                                required: "Username is required",
                                minLength: { value: 3, message: "At least 3 characters" },
                            })}
                        />
                        {errors.username && (
                            <span className={styles.error}>{errors.username.message}</span>
                        )}
                    </div>

                    {/* Email */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            {...register("email", {
                                required: "Email is required",
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: "Invalid email address",
                                },
                            })}
                        />
                        {errors.email && (
                            <span className={styles.error}>{errors.email.message}</span>
                        )}
                    </div>

                    {/* Confirm Email */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="confirmEmail">Confirm Email</label>
                        <input
                            id="confirmEmail"
                            type="email"
                            placeholder="you@example.com"
                            {...register("confirmEmail", {
                                required: "Please confirm your email",
                                validate: (val) =>
                                    val === email || "Email addresses do not match",
                            })}
                        />
                        {errors.confirmEmail && (
                            <span className={styles.error}>{errors.confirmEmail.message}</span>
                        )}
                    </div>

                    {/* Password */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="password">Password</label>
                        <PasswordInput
                            id="password"
                            placeholder="••••••••"
                            {...register("password", passwordRules)}
                        />
                        {errors.password && (
                            <span className={styles.error}>{errors.password.message}</span>
                        )}

                        {/* Strength Meter */}
                        {password && (
                            <div className={styles.strengthMeter}>
                                <div className={styles.strengthTrack}>
                                    <div
                                        className={styles.strengthBar}
                                        style={{
                                            width: `${(strength / 5) * 100}%`,
                                            backgroundColor: strengthColor,
                                        }}
                                    />
                                </div>
                                <span
                                    className={styles.strengthLabel}
                                    style={{ color: strengthColor }}
                                >
                                    {strengthLabel}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <PasswordInput
                            id="confirmPassword"
                            placeholder="••••••••"
                            {...register("confirmPassword", {
                                required: "Please confirm password",
                                validate: (val) =>
                                    val === watch("password") || "Passwords do not match",
                            })}
                        />
                        {errors.confirmPassword && (
                            <span className={styles.error}>
                                {errors.confirmPassword.message}
                            </span>
                        )}
                    </div>

                    {/* Date of Birth */}
                    <div className={styles.inputGroup}>
                        <label htmlFor="dateOfBirth">Date of Birth</label>
                        <input
                            id="dateOfBirth"
                            type="date"
                            max={new Date().toISOString().split("T")[0]}
                            min={`${new Date().getFullYear() - 120}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
                            {...register("dateOfBirth", {
                                required: "Date of birth is required",
                                validate: (val) => {
                                    const today = new Date();
                                    const dob = new Date(val);
                                    if (dob > today) {
                                        return "Date of birth is invalid";
                                    }
                                    if (calculateAge(val) < 13) {
                                        return "You do not meet the minimum age requirement";
                                    }
                                    return true;
                                },
                            })}
                        />
                        {errors.dateOfBirth && (
                            <span className={styles.error}>{errors.dateOfBirth.message}</span>
                        )}
                    </div>

                    {/* Terms of Service */}
                    <div className={styles.checkboxGroup}>
                        <input
                            id="termsAccepted"
                            type="checkbox"
                            {...register("termsAccepted", {
                                required: "You must accept the Terms of Service",
                            })}
                        />
                        <label htmlFor="termsAccepted">
                            I accept the <a href="/terms">Terms of Service</a>
                        </label>
                        {errors.termsAccepted && (
                            <span className={styles.error}>{errors.termsAccepted.message}</span>
                        )}
                    </div>

                    <button type="submit" className={styles.button}>
                        Sign Up
                    </button>
                </form>

                <div className={styles.footer}>
                    Already have an account?{" "}
                    <a href="/signin">Sign In</a>
                </div>
                <div className={styles.footer}>
                    <a href="/privacy">Privacy Policy</a>
                </div>
            </div>
        </div>
    );
};

export default SignUp;
